// CI/CD pipeline test
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Suspense, lazy, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "@/components/ErrorFallback";

import Index from "./pages/Index";
const OldLandingPage = lazy(() => import("./pages/OldLandingPage"));
import Portal from "./pages/admin_dashboard/AdminDashboard";
import Auth from "./pages/user_auth/Auth";
import Admin from "./pages/admin_dashboard/Admin";
import ListingBooking from "./pages/listing_booking/ListingBooking";
import ModifyBooking from "./pages/modify_booking/ModifyBooking";
import BookingConfirmed from "./pages/booking_confirmed/BookingConfirmed";
import CancelBooking from "./pages/cancel_booking/CancelBooking";
import WaitlistClaim from "./pages/waitlist_claim/WaitlistClaim";
import MakeUpClaim from "./pages/makeup_claim/MakeUpClaim";
import CustomerFeedback from "./pages/customer_feedback/CustomerFeedback";
import ForYouthWaitlist from "./pages/for_youth/ForYouthWaitlist";
import NotFound from "./pages/NotFound";
// Dev-only stage for the Workflow Copilot panel. Lazy so prod bundles skip it.
const CopilotPreview = lazy(() => import("./pages/dev/CopilotPreview"));
// Dev-only stage for external connectors preview.
const ConnectorsPreview = lazy(() => import("./pages/dev/ConnectorsPreview"));
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataDeletion from "./pages/DataDeletion";
import AboutUs from "./pages/AboutUs";
import Pricing from "./pages/Pricing";
import Onboarding from "./pages/onboarding/Onboarding";
import EnterpriseSuccess from "./pages/onboarding/EnterpriseSuccess";
import OnboardingSuccess from "./pages/onboarding/OnboardingSuccess";
import VerifySuccess from "./pages/user_auth/VerifySuccess";
import StaffInvite from "./pages/user_auth/StaffInvite";
import ResetPassword from "./pages/user_auth/ResetPassword";
import IntegrationResult from "./pages/onboarding/IntegrationResult";
import CollaboratorAccessApprove from "./pages/support_access/CollaboratorAccessApprove";
import DesignSystem from "./pages/DesignSystem";
import InternalHome from "./pages/internal/InternalHome";
import OnboardingWizard from "./pages/internal/OnboardingWizard";
import CollaboratorInvitePanel from "./components/internal/CollaboratorInvitePanel";
import InternalAccessGate from "./components/internal/InternalAccessGate";
import BlogList from "./pages/blog/BlogList";
import BlogPost from "./pages/blog/BlogPost";
import GoogleBusinessCallback from "./pages/GoogleBusinessCallback";
import ComplaintRecovery from "./pages/ComplaintRecovery";
import { buildAdminUrl, legacySectionParams } from "@/lib/adminOrigin";
import { DEMO_BASE_PATH, isDemoMode } from "@/lib/demo/demoMode";
import DemoRoutes from "@/lib/demo/DemoRoutes";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { CookieConsentBanner } from "./components/CookieConsentBanner";

const queryClient = new QueryClient();

const ScrollToHash = () => {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    const id = location.hash.slice(1);
    const scrollToTarget = () => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timeout = window.setTimeout(scrollToTarget, 50);
    return () => window.clearTimeout(timeout);
  }, [location.pathname, location.hash]);

  return null;
};

const RedirectToAdmin = () => {
  useEffect(() => {
    window.location.replace(buildAdminUrl(window.location, "/"));
  }, []);
  return null;
};

// Old dashboard emails linked the public site with `?section=` (e.g. the
// approval reminder's "Review them now"). Send those on to the dashboard page
// they meant instead of showing the landing page.
const IndexOrLegacyDashboardRedirect = () => {
  const legacyQuery =
    legacySectionParams(new URLSearchParams(window.location.search))?.toString() ?? null;
  useEffect(() => {
    if (legacyQuery === null) return;
    const { protocol, hostname, port, hash, origin } = window.location;
    window.location.replace(
      buildAdminUrl({ protocol, hostname, port, hash, origin, search: `?${legacyQuery}` }, "/"),
    );
  }, [legacyQuery]);
  return legacyQuery === null ? <Index /> : null;
};

const RedirectToAdminAuth = () => {
  useEffect(() => {
    window.location.replace(buildAdminUrl(window.location, "/auth"));
  }, []);
  return null;
};

// Staff invitation links point at the admin origin, but a forwarded email or a
// hand-typed URL can land on the marketing host. Carry the token across.
const RedirectToAdminStaffInvite = () => {
  useEffect(() => {
    // buildAdminUrl carries the current query string over, so the token comes
    // along without being appended twice.
    window.location.replace(buildAdminUrl(window.location, "/staff-invite"));
  }, []);
  return null;
};

import { OutletProvider } from "@/contexts/OutletContext";
import { TerminologyProvider } from "@/contexts/TerminologyContext";

const PRIVATE_PATH_PREFIXES = [
  "/book/", "/listing/", "/modify-booking", "/booking-confirmed", "/cancel-booking",
  "/waitlist/", "/makeup/", "/feedback", "/recover", "/staff-invite", "/auth", "/admin",
];

const NoIndexPrivateRoutes = () => {
  const { pathname } = useLocation();
  if (!PRIVATE_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return null;
  return (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );
};

const App = () => {
  // `?subdomain=internal|admin` is a dev-only escape hatch so the internal tool
  // and admin app can be previewed locally without `internal.`/`admin.` DNS.
  const devSubdomainOverride =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get('subdomain');
  const isAdminSubdomain =
    window.location.hostname.startsWith('admin.') ||
    devSubdomainOverride === 'admin';
  const isInternalSubdomain =
    window.location.hostname.startsWith('internal.') ||
    devSubdomainOverride === 'internal';
  // Public read-only demo of the dashboard at /demo/enrichment.
  const isDemo = isDemoMode();

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
      <TooltipProvider>
        <AuthProvider>
          <TerminologyProvider>
            <OutletProvider>
            <Sonner />
            <Sentry.ErrorBoundary
              fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
              showDialog={false}
            >
              <BrowserRouter basename={isDemo ? DEMO_BASE_PATH : undefined}>
              {isDemo ? (
                <DemoRoutes />
              ) : isInternalSubdomain ? (
                <InternalAccessGate>
                  <Routes>
                    <Route path="/" element={<InternalHome />} />
                    <Route path="/design" element={<DesignSystem />} />
                    <Route path="/onboarding" element={<OnboardingWizard />} />
                    <Route path="/collaborator-access" element={<CollaboratorInvitePanel />} />
                    <Route path="*" element={<InternalHome />} />
                  </Routes>
                </InternalAccessGate>
              ) : isAdminSubdomain ? (
                <Routes>
                  <Route path="/" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                  <Route path="/onboarding-success" element={<ProtectedRoute><OnboardingSuccess /></ProtectedRoute>} />
                  <Route path="/enterprise-thanks" element={<ProtectedRoute><EnterpriseSuccess /></ProtectedRoute>} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth/verify-success" element={<VerifySuccess />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route path="/staff-invite" element={<StaffInvite />} />
                  <Route path="/integration" element={<ProtectedRoute><IntegrationResult /></ProtectedRoute>} />
                  <Route path="/collaborator-access/approve" element={<ProtectedRoute><CollaboratorAccessApprove /></ProtectedRoute>} />
                  <Route path="/oauth/google-business" element={<ProtectedRoute><GoogleBusinessCallback /></ProtectedRoute>} />
                  {/* Dev-only: review the Workflow Copilot UI without signing in. */}
                  {import.meta.env.DEV && (
                    <>
                      <Route
                        path="/dev/copilot-preview"
                        element={
                          <Suspense fallback={null}>
                            <CopilotPreview />
                          </Suspense>
                        }
                      />
                      <Route
                        path="/dev/connectors-preview"
                        element={
                          <Suspense fallback={null}>
                            <ConnectorsPreview />
                          </Suspense>
                        }
                      />
                    </>
                  )}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              ) : (
                <>
                  <ScrollToHash />
                  <NoIndexPrivateRoutes />
                  <Routes>
                    <Route path="/" element={<IndexOrLegacyDashboardRedirect />} />
                    <Route path="/old-landingpage" element={<Suspense fallback={null}><OldLandingPage /></Suspense>} />
                    <Route path="/book/:id" element={<Portal />} />
                    <Route path="/listing/:id" element={<ListingBooking />} />
                    <Route path="/modify-booking/:bookingId" element={<ModifyBooking />} />
                    <Route path="/booking-confirmed/:bookingId?" element={<BookingConfirmed />} />
                    <Route path="/cancel-booking/:bookingId?" element={<CancelBooking />} />
                    <Route path="/waitlist/claim/:token" element={<WaitlistClaim />} />
                    <Route path="/makeup/claim/:token" element={<MakeUpClaim />} />
                    <Route path="/feedback" element={<CustomerFeedback />} />
                    <Route path="/recover" element={<ComplaintRecovery />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/blog" element={<BlogList />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    {/* Dibs for Youth launch waitlist. A static path, so it outranks the /:slug storefronts. */}
                    <Route path="/foryouth" element={<ForYouthWaitlist />} />
                    <Route path="/staff-invite" element={<RedirectToAdminStaffInvite />} />
                    <Route path="/:slug" element={<Portal />} />
                    <Route path="/auth" element={<RedirectToAdminAuth />} />
                    <Route path="/auth/verify-success" element={<VerifySuccess />} />
                    <Route path="/auth/reset-password" element={<ResetPassword />} />
                    <Route path="/admin" element={<RedirectToAdmin />} />
                    {import.meta.env.DEV && (
                      <Route
                        path="/dev/connectors-preview"
                        element={
                          <Suspense fallback={null}>
                            <ConnectorsPreview />
                          </Suspense>
                        }
                      />
                    )}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </>
              )}
              {!isDemo && <CookieConsentBanner />}
            </BrowserRouter>
            </Sentry.ErrorBoundary>
            </OutletProvider>
          </TerminologyProvider>
        </AuthProvider>
      </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
